import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import yup from "../../formsvalidation/yup";
import { SimpleCross } from "../../assets/icons";
import ButtonLoader from "../../atoms/buttonLoader";

const CreateManagerSidebar = ({ visible, onClose, managerData, onSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { commonValidations } = yup;

  const formik = useFormik({
    initialValues: {
      name: managerData?.name || "",
      email: managerData?.email || "",
      password: "",
      role: managerData?.role || "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: commonValidations.name(),
      email: commonValidations.email(),
      ...(managerData ? {} : { 
        password: Yup.string()
          .min(8, "Password must be at least 8 characters")
          .matches(/[a-z]/, "Password must contain at least one lowercase letter")
          .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
          .matches(/[0-9]/, "Password must contain at least one number")
          .matches(/[!@#$%^&*(),.?":{}|<>]/, "Password must contain at least one special character")
          .test('min-digits', 'Password must contain at least 2 digits', function(value) {
            if (!value) return true;
            const digitCount = (value.match(/[0-9]/g) || []).length;
            return digitCount >= 2;
          })
          .test('no-spaces', 'Password cannot contain spaces', function(value) {
            if (!value) return true;
            return !value.includes(' ');
          })
          .required("Password is required")
      }),
      role: commonValidations.role(),
    }),
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        await onSuccess(values);
      } catch (error) {
        console.error("Error saving manager:", error);
      } finally {
        setIsLoading(false);
      }
    },
  });

  if (!visible) return null;

  return (
    <div className="fixed top-0 right-0 w-[400px] h-full bg-white shadow-lg z-50">
      <div className="flex justify-between items-center p-4">
        <h2 className="text-[20px] font-semibold text-[#1E3A8A]">
          {managerData ? "Update Manager" : "Add Manager"}
        </h2>
        <button className="text-gray-500 hover:text-gray-700" onClick={onClose}>
          {SimpleCross}
        </button>
      </div>

      <form onSubmit={formik.handleSubmit} className="p-4 -mt-5 flex flex-col gap-4 h-[calc(100vh-120px)] overflow-y-auto">
        <div>
          <input
            type="text"
            name="name"
            placeholder="Full Name"
            className={`w-full h-[52px] px-3 border rounded focus:outline-none focus:ring-2 focus:ring-[#1E3A8A] ${
              formik.touched.name && formik.errors.name
                ? "border-red-500"
                : "border-gray-300"
            }`}
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.touched.name && formik.errors.name && (
            <p className="text-red-500 text-sm mt-1">{formik.errors.name}</p>
          )}
        </div>

        <div>
          <input
            type="email"
            name="email"
            placeholder="Email"
            className={`w-full h-[52px] px-3 border rounded focus:outline-none focus:ring-2 focus:ring-[#1E3A8A] ${
              formik.touched.email && formik.errors.email
                ? "border-red-500"
                : "border-gray-300"
            }`}
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.touched.email && formik.errors.email && (
            <p className="text-red-500 text-sm mt-1">{formik.errors.email}</p>
          )}
        </div>

        {!managerData && (
          <div>
            <input
              type="password"
              name="password"
              placeholder="Password"
              className={`w-full h-[52px] px-3 border rounded focus:outline-none focus:ring-2 focus:ring-[#1E3A8A] ${
                formik.touched.password && formik.errors.password
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.password && formik.errors.password && (
              <p className="text-red-500 text-sm mt-1">{formik.errors.password}</p>
            )}
          </div>
        )}

        <div>
          <select
            name="role"
            className={`w-full h-[52px] px-3 border rounded focus:outline-none focus:ring-2 focus:ring-[#1E3A8A] ${
              formik.touched.role && formik.errors.role
                ? "border-red-500"
                : "border-gray-300"
            }`}
            value={formik.values.role}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          >
            <option value="">Select Role</option>
            <option value="Admin">Admin</option>
            <option value="Project Manager">Project Manager</option>
            <option value="Site Manager">Site Manager</option>
          </select>
          {formik.touched.role && formik.errors.role && (
            <p className="text-red-500 text-sm mt-1">{formik.errors.role}</p>
          )}
        </div>

        <button
          type="submit"
          className="w-[152px] h-[44px] bg-[#F97316] text-white font-medium text-[20px] rounded-[4px] hover:bg-orange-600 whitespace-nowrap"
        >
          {isLoading ? <ButtonLoader /> : managerData ? "Update Manager" : "Add Manager"}
        </button>
      </form>
    </div>
  );
};

export default CreateManagerSidebar; 